/**
 * AmplifierCard — celebratory unlock card shown on the Referrals page after
 * the user hits 3 paid conversions. Pre-fills a tweet draft they can publish
 * with one click.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Twitter, X, Copy, Check, Loader2, Sparkles, Trophy } from "lucide-react";

export default function AmplifierCard({ amplifier, onDismissed }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!amplifier?.unlocked || amplifier.dismissed_at) return null;

  const loadDraft = async () => {
    if (draft) return;
    setLoading(true);
    try {
      const r = await api.get("/referrals/amplifier/tweet-draft");
      setDraft(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load tweet draft");
    } finally { setLoading(false); }
  };

  const copyDraft = () => {
    if (!draft) return;
    navigator.clipboard.writeText(draft.text);
    setCopied(true);
    toast.success("Tweet copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/referrals/amplifier/dismiss");
      onDismissed?.();
    } finally { setBusy(false); }
  };

  return (
    <div
      className="relative card overflow-hidden mb-5 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-l-4 border-l-amber-500"
      data-testid="amplifier-card"
    >
      <button
        type="button"
        onClick={dismiss}
        disabled={busy}
        className="absolute top-3 right-3 text-[var(--ink-3)] hover:text-[var(--ink)] z-10"
        title="Hide this"
        data-testid="amplifier-dismiss"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
      </button>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-md">
            <Trophy size={16} fill="currentColor" />
          </div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
            Amplifier unlocked · {amplifier.threshold}+ conversions
          </div>
        </div>
        <div className="font-display font-black text-2xl tracking-tight text-[var(--ink)] mb-2">
          Brag a little. Your network is converting.
        </div>
        <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-4 max-w-2xl">
          Three of your invitees just upgraded — that's social proof gold. Drop a tweet, watch the next batch roll in, and your bonus-months balance with it.
        </p>

        {!draft ? (
          <button
            type="button"
            onClick={loadDraft}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[var(--ink)] text-white font-bold px-4 py-2.5 rounded-lg hover:opacity-90"
            data-testid="amplifier-load-draft"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Show me my tweet draft
          </button>
        ) : (
          <>
            <div
              className="rounded-lg bg-white border border-[var(--line)] p-3.5 mb-3 whitespace-pre-wrap text-sm leading-relaxed text-[var(--ink)]"
              data-testid="amplifier-draft-text"
            >
              {draft.text}
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={draft.intent_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black text-white font-bold px-4 py-2.5 rounded-lg hover:opacity-90"
                data-testid="amplifier-post-tweet"
              >
                <Twitter size={14} />
                Post on X
              </a>
              <button
                type="button"
                onClick={copyDraft}
                className="btn-outline text-sm"
                data-testid="amplifier-copy-draft"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy text"}
              </button>
            </div>
            <div className="text-[11px] text-[var(--ink-3)] mt-2.5 leading-relaxed">
              Heads up — the draft is yours. Tweak it before posting if you want to ditch the marketing voice and sound more like you.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
